<template>
  <div>
    <px-resposta-negociacao-cadastro-dados-basicos ref="negociacaoDadosBasicos"
                                                   :negociacao="negociacao"
                                                   :dialog="dialog"/>
    <v-card class="mt-1 rounded-tag-small" outlined>
      <v-simple-table dense>
        <template v-slot:default>
          <tbody>
            <px-resposta-negociacao-cadastro-produtos-table-row
              v-for="(produto, index) in produtos"
              :key="produto.id"
              :produto="produto"
              :moeda="resposta.moeda"
              :id-fornecedor="resposta.fornecedor.id"
              :show-divider="showDivider(index)"/>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
    <v-row justify="center">
      <v-col cols="12" sm="11">
        <v-textarea
          outlined
          auto-grow
          class="required rounded-tag-small"
          label="Mensagem para o fornecedor"
          counter="1000"
          rows="2"
          :error="$v.negociacao.message.$error"
          @blur="$v.negociacao.message.$touch()"
          v-model="negociacao.message"
        />
      </v-col>
      <v-col cols="12" sm="1">
        <div class="pa-0 mt-3">
          <px-anexo v-model="negociacao.arquivos" :max="3" @loading="onFileLoad"/>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="text-right">
        <v-btn class="mr-3 normal-btn-text"
               text
               :disabled="loading"
               @click="onClickCancelar">
          Cancelar
        </v-btn>
        <v-btn color="primary"
               class="normal-btn-text rounded-tag-small elevation-0"
               :loading="loading"
               :disabled="$v.$invalid || $refs.negociacaoDadosBasicos.$v.$invalid || hasFileLoading"
               @click="onClickEnviar">
          Enviar
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>


<script>
import { required } from 'vuelidate/lib/validators';
import {
  TipoNegociacao,
  SituacaoRespostaCotacaoProduto,
  SituacoesCotacao,
  CampoNegociacao,
} from 'px-business-components';
import { cloneDeep } from 'lodash';
import NegociacaoService from '../negociacao-service';
import RespostaNegociacaoCadastroDadosBasicos from './RespostaNegociacaoCadastroDadosBasicos.vue';
import RespostaNegociacaoCadastroProdutosTableRow from './RespostaNegociacaoCadastroProdutosTableRow.vue';
import { getCamposNegociacaoProduto } from './negociacao-utils';

const initalState = () => ({
  novoTipoFrete: null,
  novoPrazoEntrega: null,
  novaFormaPagemento: null,
  novoPrazoPagamento: null,
  produtos: [],
  arquivos: [],
  message: null,
});

export default {
  props: {
    resposta: {
      type: Object,
      required: true,
    },
    dialog: Boolean,
  },
  data() {
    return {
      loading: false,
      hasFileLoading: false,
      produtos: [],
      initialProdutos: [],
      negociacao: initalState(),
    };
  },
  computed: {
    showDivider() {
      return index => index !== (this.produtos.length - 1);
    },
  },
  methods: {
    onClickCancelar() {
      this.$emit('cancel');
    },
    onFileLoad(loading) {
      this.hasFileLoading = loading;
    },
    buildCamposNegociacao() {
      const negociacao = {};
      const campos = [];
      CampoNegociacao.values.forEach((value) => {
        if (this.negociacao[value.newField]) {
          negociacao[value.newField] = this.negociacao[value.newField];
          campos.push(value.key);
        }
      });
      negociacao.campos = campos;
      return negociacao;
    },
    buildProdutosNegociacao() {
      const selected = this.produtos.filter(produto => produto.$$active);
      return selected.map(produto => ({
        respostaProduto: {
          id: produto.id,
        },
        negociacao: {
          campos: getCamposNegociacaoProduto(produto, this.initialProdutos),
          novoPrecoUnitario: produto.$$precoUnitario,
          novoArquivosProduto: produto.$$arquivosProduto,
          novaEspecificacaoProduto: produto.$$especificacaoProduto,
          novoIcms: produto.$$icms,
          novoIpi: produto.$$ipi,
        },
        arquivos: produto.$$arquivosProduto,
      }));
    },
    buildNegociacao() {
      return {
        resposta: {
          id: this.resposta.id,
        },
        tipo: TipoNegociacao.keys.SOLICITACAO.key,
        message: this.negociacao.message,
        negociacao: this.buildCamposNegociacao(),
        produtos: this.buildProdutosNegociacao(),
        arquivos: this.negociacao.arquivos,
        percentualDesconto: this.negociacao.percentualDesconto,
      };
    },
    solicitarNegociacao() {
      this.loading = true;
      const negociacao = this.buildNegociacao();
      NegociacaoService.solicitar(this.resposta.cotacao.id, this.resposta.id, negociacao)
        .then(() => {
          this.$toast('Negociação enviada com sucesso', { color: 'success' });
          this.$emit('negociacao-enviada');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onClickEnviar() {
      if (this.isFileProdutoUploading()) {
        this.$toast('Aguarde a finalização do envio do(s) arquivo(s)', { color: 'error' });
        return;
      }
      this.solicitarNegociacao();
    },
    isProdutoValido(produto) {
      return produto.situacao === SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key
        && SituacoesCotacao.ABERTAS.includes(produto.cotacaoProduto.situacao);
    },
    isFileProdutoUploading() {
      const filesUploading = this.produtos.map(c => c.$$hasFileLoading);
      return filesUploading.find(f => f);
    },
    mapProduto(produto) {
      return {
        id: produto.id,
        produto: produto.cotacaoProduto.produto,
        $$precoUnitario: produto.precoUnitario,
        $$arquivosProduto: produto.cotacaoProduto.arquivos || [],
        $$especificacaoProduto: produto.especificacaoProduto,
        $$especificacaoProdutoOrigem: produto.cotacaoProduto.especificacaoProduto,
        $$ipi: produto.impostos.ipi,
        $$icms: produto.impostos.icms,
        $$active: false,
      };
    },
  },
  created() {
    const produtosValidos = this.resposta.produtos.filter(produto => this.isProdutoValido(produto));
    this.produtos = produtosValidos.map(prod => this.mapProduto(prod));
    this.initialProdutos = cloneDeep(this.resposta.produtos);
  },
  validations: {
    negociacao: {
      message: {
        required,
      },
    },
  },
  components: {
    pxRespostaNegociacaoCadastroDadosBasicos: RespostaNegociacaoCadastroDadosBasicos,
    pxRespostaNegociacaoCadastroProdutosTableRow: RespostaNegociacaoCadastroProdutosTableRow,
  },
};
</script>
